import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowUpRight, MagnifyingGlass, ShieldCheck } from '@phosphor-icons/react';
import { PageHero, Reveal } from '../lib/motion';
import { SERVICES, COUNTRIES } from '../data/site';
import { API } from '../lib/api';
import { useSEO } from '../lib/seo';

const filterCls =
  'w-full border border-line bg-ink px-4 py-3 text-sm text-parchment placeholder:text-steel/50 focus:border-volt focus:outline-none';

const Directory = () => {
  useSEO({
    title: 'Provider Directory — Verified Industrial Service Providers | Vox Meridian',
    description:
      'Browse founder-verified industrial and business service providers across 10 markets — machining, MRO, calibration, electrical, automation and more. Contact providers directly.',
    path: '/directory',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Vox Meridian Provider Directory',
      description: 'Founder-verified industrial and business service providers across 10 markets.',
      url: 'https://voxmeridian.com/directory',
      isPartOf: { '@type': 'WebSite', name: 'Vox Meridian', url: 'https://voxmeridian.com' },
    },
  });

  const [entries, setEntries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState('');
  const [trade, setTrade] = useState('');
  const [country, setCountry] = useState('');

  useEffect(() => {
    const params = {};
    if (q) params.q = q;
    if (trade) params.trade = trade;
    if (country) params.country = country;
    const t = setTimeout(() => {
      axios
        .get(`${API}/directory`, { params })
        .then((r) => setEntries(r.data.entries || []))
        .catch(() => {})
        .finally(() => setLoaded(true));
    }, 250);
    return () => clearTimeout(t);
  }, [q, trade, country]);

  const trades = useMemo(() => SERVICES.map((s) => s.name), []);

  return (
    <div className="bg-ink" data-testid="directory-page">
      <PageHero
        eyebrow="Provider directory"
        lines={[<>Verified providers.</>, <span key="l2">On the <span className="text-volt">record.</span></span>]}
        sub="Every company here has been reviewed by the founder — registration checked, capability verified. Find a provider, read their profile, and contact them directly. No commission, no middleman."
      />

      <section className="border-b border-line bg-ink" data-testid="directory-filters">
        <div className="mx-auto max-w-7xl px-6 py-8 lg:px-10">
          <div className="grid gap-3 sm:grid-cols-[2fr_1fr_1fr]">
            <div className="relative">
              <MagnifyingGlass size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-steel" />
              <input
                className={`${filterCls} pl-11`}
                placeholder="Search companies, trades, keywords…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="directory-search-input"
              />
            </div>
            <select className={filterCls} value={trade} onChange={(e) => setTrade(e.target.value)} data-testid="directory-trade-select">
              <option value="">All trades</option>
              {trades.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select className={filterCls} value={country} onChange={(e) => setCountry(e.target.value)} data-testid="directory-country-select">
              <option value="">All markets</option>
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
          {!loaded ? (
            <p className="mono-label text-steel" data-testid="directory-loading">Loading providers…</p>
          ) : entries.length === 0 ? (
            <Reveal>
              <div className="border border-volt/40 bg-surface p-10 sm:p-14" data-testid="directory-empty">
                <p className="mono-label text-volt">The directory is filling</p>
                <h2 className="mt-4 max-w-2xl font-display text-3xl font-black tracking-tight text-parchment sm:text-4xl">
                  The first verified providers are being reviewed now.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-steel">
                  Every profile is checked by the founder before it goes public — registration, capability
                  and contact details. Run an industrial service business? Get verified and claim your
                  public profile.
                </p>
                <Link
                  to="/providers"
                  data-testid="directory-empty-cta"
                  className="group mt-8 inline-flex items-center gap-2 bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark"
                >
                  Get verified
                  <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </Reveal>
          ) : (
            <>
              <p className="mono-label text-steel" data-testid="directory-count">
                {entries.length} verified {entries.length === 1 ? 'provider' : 'providers'}
              </p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {entries.map((e, i) => (
                  <Reveal key={e.slug} delay={Math.min(i * 0.05, 0.4)} y={16}>
                    <Link
                      to={`/directory/${e.slug}`}
                      data-testid={`directory-card-${e.slug}`}
                      className="group flex h-full flex-col border border-line bg-surface p-6 transition-colors duration-300 hover:border-volt/60"
                    >
                      <div className="flex items-start justify-between gap-3">
                        {e.has_photo ? (
                          <img
                            src={`${API}/directory/photo/${e.slug}`}
                            alt={`${e.company} logo`}
                            className="h-12 w-12 border border-line object-cover"
                            loading="lazy"
                            data-testid={`directory-photo-${e.slug}`}
                          />
                        ) : (
                          <span className="flex h-12 w-12 items-center justify-center border border-line bg-ink font-display text-lg font-black text-volt">
                            {e.company.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-volt">
                          <ShieldCheck size={13} weight="bold" /> Verified
                        </span>
                      </div>
                      <p className="mt-4 font-display text-lg font-bold text-parchment transition-colors duration-200 group-hover:text-volt">
                        {e.company}
                      </p>
                      <p className="mt-1 font-mono text-[11px] uppercase tracking-[0.15em] text-steel">
                        {e.country_name}
                        {e.type === 'showcase' ? ' · Showcase' : ''}
                      </p>
                      {e.tagline && <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{e.tagline}</p>}
                      <div className="mt-4 flex flex-wrap gap-1.5">
                        {e.trades.slice(0, 3).map((t) => (
                          <span key={t} className="border border-line px-2 py-1 text-[11px] text-steel">{t}</span>
                        ))}
                        {e.trades.length > 3 && (
                          <span className="border border-line px-2 py-1 text-[11px] text-steel">+{e.trades.length - 3} more</span>
                        )}
                      </div>
                      <span className="mt-4 flex items-center gap-1.5 border-t border-line pt-4 font-mono text-[11px] font-bold uppercase tracking-[0.15em] text-volt">
                        View profile
                        <ArrowUpRight size={13} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </span>
                    </Link>
                  </Reveal>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <section className="bg-surface" data-testid="directory-bottom-cta">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-8 px-6 py-16 sm:flex-row sm:items-center sm:justify-between lg:px-10">
          <div>
            <p className="mono-label text-volt">Can't find your trade?</p>
            <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-parchment sm:text-4xl">
              Post one RFQ — the founder sources for you.
            </h2>
          </div>
          <Link
            to="/buyers"
            data-testid="directory-rfq-cta"
            className="group flex shrink-0 items-center gap-2 bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark"
          >
            Post an RFQ — free
            <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Directory;
